/*
* === BursaryManagementPage.js (Added ReactQuill Rich Text Editor & Tenant Config) ===
*/
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Container, Typography, Card, Box, Button, Alert, Dialog, DialogActions,
    DialogContent, DialogTitle, TextField, IconButton, Switch, FormControlLabel,
    Grid, Paper, CardContent as MuiCardContent, Chip, Divider, CircularProgress, CardActionArea,
    List, ListItem, ListItemText, ListSubheader, Snackbar, Tooltip, DialogContentText,
    Tabs, Tab, FormControl, InputLabel, Select, MenuItem, Badge
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { tenantConfig } from '../../config/tenantConfig'; // 🚨 IMPORT TENANT CONFIG

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const FUND_CATEGORIES = [
    'General', 'Engineering', 'IT & Computer Science', 'Health Sciences', 
    'Commerce & Finance', 'Humanities', 'Education', 'Arts & Design', 'Law', 'Science'
];

const RubricEditorModal = ({ open, onClose, fund, token }) => {
    const [criteria, setCriteria] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [newCriterion, setNewCriterion] = useState({ CriteriaName: '', MaxScore: 10 });

    const fetchRubric = useCallback(async () => {
        if (!fund?.Id) return;
        setLoading(true);
        try {
            const response = await api.get(`/admin/bursaries/${fund.Id}/rubric`, { headers: { Authorization: `Bearer ${token}` } });
            setCriteria(response.data);
        } catch (err) {
            setError('Failed to load rubric.');
        } finally {
            setLoading(false);
        }
    }, [fund, token]);

    useEffect(() => {
        if (open) {
            fetchRubric();
        }
    }, [open, fetchRubric]);

    const handleAddCriterion = async () => {
        setError('');
        try {
            await api.post(`/admin/bursaries/${fund.Id}/rubric`, newCriterion, { headers: { Authorization: `Bearer ${token}` } });
            setNewCriterion({ CriteriaName: '', MaxScore: 10 });
            fetchRubric(); 
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add criterion.');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Manage Scoring Rubric for: {fund?.FundName}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <List subheader={<ListSubheader>Existing Criteria</ListSubheader>}>
                    {loading ? <CircularProgress /> :
                        criteria.length === 0 ? <ListItem><ListItemText primary="No criteria created yet." /></ListItem> :
                        criteria.map(c => (
                            <ListItem key={c.Id} divider>
                                <ListItemText primary={c.CriteriaName} secondary={`Max Score: ${c.MaxScore}`} />
                            </ListItem>
                        ))
                    }
                </List>
                
                <Divider sx={{ my: 2 }}><Chip label="Add New Criterion" /></Divider>
                <Box component="form" sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Criterion Name"
                        value={newCriterion.CriteriaName}
                        onChange={(e) => setNewCriterion(prev => ({...prev, CriteriaName: e.target.value}))}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Max Score"
                        type="number"
                        value={newCriterion.MaxScore}
                        onChange={(e) => setNewCriterion(prev => ({...prev, MaxScore: e.target.value}))}
                        size="small"
                        sx={{ width: '150px' }}
                    />
                    <Button variant="contained" onClick={handleAddCriterion}>Add</Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const BursaryInfoCard = ({ bursary, onOpenEdit, onOpenRubric, onClone, onArchive, onUnarchive, onDelete, currentUserRole }) => {
    const isActive = bursary.IsActive == 1 || bursary.IsActive === true;
    const endDate = new Date(bursary.ApplicationEndDate);
    const isPast = endDate < new Date();

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: isActive ? 1 : 0.8 }}>
            <CardActionArea onClick={() => onOpenEdit(bursary)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <MuiCardContent sx={{ flexGrow: 1, width: '100%', pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1, gap: 1 }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', lineHeight: 1.2, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {bursary.FundName}
                        </Typography>
                        <Box sx={{ flexShrink: 0, minWidth: 80 }}>
                            <Chip label={isActive ? (isPast ? 'Closed' : 'Active') : 'Archived'} color={isActive ? (isPast ? 'default' : 'success') : 'default'} size="small" sx={{ height: 24, fontWeight: 'bold' }} />
                        </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Category: <strong>{bursary.FundCategory || 'General'}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Sponsor: {bursary.Sponsor}
                    </Typography>

                    {currentUserRole === 'SuperUser' && (
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>MANAGED BY:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{bursary.CreatorName || 'Unknown Admin'}</Typography>
                            <Typography variant="body2" color="text.secondary">{bursary.CompanyName || 'No Company Assigned'}</Typography>
                        </Box>
                    )}
                    
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        <strong>Closes:</strong> {bursary.ApplicationEndDate ? endDate.toLocaleDateString('en-ZA') : 'No Date Set'}
                    </Typography>
                    
                    {/* 🚨 TENANT FEATURE TOGGLE: Hide Amount if requested */}
                    {tenantConfig.features.showFundAmount !== false && (
                        <>
                            <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>R {Number(bursary.TotalAmount).toLocaleString('en-ZA')}</Typography>
                            <Typography variant="caption" color="text.secondary">Total Fund</Typography>
                        </>
                    )}
                </MuiCardContent>
            </CardActionArea>
            
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider' }}>
                <Box>
                    {isActive ? (
                        <Tooltip title="Archive"><IconButton onClick={() => onArchive(bursary)} color="warning"><ArchiveIcon /></IconButton></Tooltip>
                    ) : (
                        <Tooltip title="Restore"><IconButton onClick={() => onUnarchive(bursary)} color="success"><UnarchiveIcon /></IconButton></Tooltip>
                    )}
                    <Tooltip title="Clone"><IconButton onClick={() => onClone(bursary)} color="default"><ContentCopyIcon /></IconButton></Tooltip>
                </Box>
                <Box>
                    {currentUserRole === 'SuperUser' && (
                         <Tooltip title="Delete Permanently"><IconButton onClick={() => onDelete(bursary)} color="error"><DeleteForeverIcon /></IconButton></Tooltip>
                    )}
                    <Tooltip title="Rubric"><IconButton onClick={() => onOpenRubric(bursary)} color="primary"><PlaylistAddCheckIcon /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton onClick={() => onOpenEdit(bursary)}><EditIcon /></IconButton></Tooltip>
                </Box>
            </Box>
        </Card>
    );
};

const BursaryManagementPage = () => {
    const [allBursaries, setAllBursaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token, user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [tabValue, setTabValue] = useState(0); 
    const [searchParams, setSearchParams] = useSearchParams();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isRubricModalOpen, setIsRubricModalOpen] = useState(false);
    const [isCloneConfirmOpen, setIsCloneConfirmOpen] = useState(false);
    const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    
    const [selectedBursary, setSelectedBursary] = useState(null);
    const [actionBursary, setActionBursary] = useState(null);
    const [formError, setFormError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [criteriaText, setCriteriaText] = useState('');
    const [documentsText, setDocumentsText] = useState('');
    
    const [companies, setCompanies] = useState([]);

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const [companyData, setCompanyData] = useState(null);

    useEffect(() => {
        if (token) {
            api.get('/company/my-company', { headers: { Authorization: `Bearer ${token}` } })
               .then(res => setCompanyData(res.data))
               .catch(err => console.warn("Usage stats not available or unauthorized:", err));
        }
    }, [token]);

    useEffect(() => {
        if (user?.role === 'SuperUser' && token) {
            api.get('/admin/companies', { headers: { Authorization: `Bearer ${token}` } })
               .then(res => setCompanies(res.data))
               .catch(err => console.error("Failed to load companies", err));
        }
    }, [user, token]);

    const isFundsMaxed = companyData && 
                        companyData.MaxFunds !== undefined &&
                        companyData.MaxFunds !== -1 && 
                        companyData.usage?.funds >= companyData.MaxFunds;

    const fetchBursaries = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        try {
            setLoading(true);
            const response = await api.get('/admin/bursaries', { headers: { Authorization: `Bearer ${token}` } });
            setAllBursaries(response.data);
        } catch (err) { setError('Failed to fetch bursaries.'); } 
        finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchBursaries(); }, [fetchBursaries]);

    useEffect(() => {
        if (searchParams.get('action') === 'create') {
            handleOpenEditModal();
            setSearchParams({}); 
        }
    }, [searchParams, setSearchParams]);

    const activeCount = allBursaries.filter(b => b.IsActive == 1 || b.IsActive === true).length;
    const archivedCount = allBursaries.filter(b => b.IsActive == 0 || b.IsActive === false || b.IsActive == null).length;

    const filteredBursaries = useMemo(() => {
        const byTab = allBursaries.filter(b => {
            if (tabValue === 0) {
                return b.IsActive == 1 || b.IsActive === true;
            } else {
                return b.IsActive == 0 || b.IsActive === false || b.IsActive == null;
            }
        });

        if (!searchTerm) return byTab;
        return byTab.filter(bursary =>
            bursary.FundName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bursary.Sponsor.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [allBursaries, searchTerm, tabValue]);

    const handleOpenEditModal = (bursary = null) => {
        if (bursary) {
            setSelectedBursary({ ...bursary });
            let reqs = '';
            let docs = '';
            try {
                let raw = bursary.EligibilityCriteria;
                let parsed = null;
                if (typeof raw === 'object' && raw !== null) parsed = raw;
                else if (typeof raw === 'string') {
                    if (raw.startsWith('"') && raw.endsWith('"')) raw = JSON.parse(raw);
                    parsed = JSON.parse(raw);
                }
                if (parsed) {
                    if (Array.isArray(parsed)) reqs = parsed.join('\n');
                    else {
                        reqs = Array.isArray(parsed.requirements) ? parsed.requirements.join('\n') : '';
                        docs = Array.isArray(parsed.documents) ? parsed.documents.join('\n') : '';
                    }
                }
            } catch (e) {
                console.warn("Could not parse criteria JSON, treating as raw text", e);
                reqs = bursary.EligibilityCriteria || '';
            }
            setCriteriaText(reqs);
            setDocumentsText(docs);
        } else {
            setSelectedBursary({ 
                FundName: '', Description: '', Sponsor: '', TotalAmount: '', 
                ApplicationStartDate: '', ApplicationEndDate: '', IsActive: true,
                FundCategory: 'General' 
            });
            setCriteriaText('');
            setDocumentsText('');
        }
        setIsEditModalOpen(true);
        setFormError('');
    };
    
    const handleOpenRubricModal = (bursary) => { setSelectedBursary(bursary); setIsRubricModalOpen(true); };
    const handleOpenCloneModal = (bursary) => { setActionBursary(bursary); setIsCloneConfirmOpen(true); };
    const handleOpenArchiveModal = (bursary) => { setActionBursary(bursary); setIsArchiveConfirmOpen(true); };
    const handleOpenDeleteModal = (bursary) => { setActionBursary(bursary); setIsDeleteConfirmOpen(true); };

    const handleCloseModals = () => {
        setIsEditModalOpen(false); setIsRubricModalOpen(false); 
        setIsCloneConfirmOpen(false); setIsArchiveConfirmOpen(false); setIsDeleteConfirmOpen(false);
        setSelectedBursary(null); setActionBursary(null);
    };

    const handleConfirmClone = async () => {
        if (!actionBursary) return;
        try {
            await api.post(`/admin/bursaries/${actionBursary.Id}/clone`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showSnackbar('Bursary cloned successfully!'); fetchBursaries(); handleCloseModals();
        } catch (err) { showSnackbar(err.response?.data?.message || 'Failed to clone bursary.', 'error'); handleCloseModals(); }
    };

    const handleConfirmArchive = async () => {
        if (!actionBursary) return;
        try {
            await api.post(`/admin/bursaries/${actionBursary.Id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showSnackbar('Bursary archived.'); fetchBursaries(); handleCloseModals();
        } catch (err) { showSnackbar('Failed to archive bursary.', 'error'); handleCloseModals(); }
    };

    const handleConfirmDelete = async () => {
        if (!actionBursary) return;
        try {
            await api.delete(`/admin/bursaries/${actionBursary.Id}`, { headers: { Authorization: `Bearer ${token}` } });
            showSnackbar('Bursary deleted permanently.'); fetchBursaries(); handleCloseModals();
        } catch (err) { showSnackbar('Failed to delete bursary.', 'error'); handleCloseModals(); }
    };

    const handleUnarchive = async (bursary) => {
        try {
            await api.post(`/admin/bursaries/${bursary.Id}/unarchive`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showSnackbar('Bursary restored to active list.'); fetchBursaries();
        } catch (err) { showSnackbar('Failed to restore bursary.', 'error'); }
    };

    const handleFormChange = (e) => {
        const { name, value, checked, type } = e.target;
        setSelectedBursary(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSaveBursary = async () => {
        const isNew = !selectedBursary.Id;
        const url = isNew ? '/admin/bursaries' : `/admin/bursaries/${selectedBursary.Id}`;
        const method = isNew ? 'post' : 'put';

        const payload = { ...selectedBursary };
        const requirementsArray = criteriaText.split('\n').map(s => s.trim()).filter(Boolean);
        const documentsArray = documentsText.split('\n').map(s => s.trim()).filter(Boolean);

        payload.EligibilityCriteria = JSON.stringify({
            requirements: requirementsArray,
            documents: documentsArray
        });

        try {
            await api[method](url, payload, { headers: { Authorization: `Bearer ${token}` } });
            fetchBursaries(); handleCloseModals(); showSnackbar(isNew ? 'Bursary created.' : 'Bursary updated.');
        } catch (err) { setFormError(err.response?.data?.message || `Failed to save bursary.`); }
    };

    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'bullet' }, { 'list': 'ordered' }],
            ['link', 'clean']
        ],
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">Bursary Management</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isFundsMaxed && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                        Fund limit reached ({companyData.usage.funds}/{companyData.MaxFunds}). <a href="/funder/billing">Upgrade Plan</a>
                    </Alert>
                )}
                
                <Button 
                    variant="contained" 
                    onClick={() => handleOpenEditModal()}
                    startIcon={<AddIcon />}
                    disabled={isFundsMaxed} 
                >
                    Create New Bursary
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={<Badge badgeContent={activeCount} color="primary" sx={{ pr: 2 }}>Active Funds</Badge>} />
                    <Tab label={<Badge badgeContent={archivedCount} color="default" sx={{ pr: 2 }}>Archived</Badge>} />
                </Tabs>
                <TextField fullWidth label="Search bursaries..." variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ endAdornment: <SearchIcon /> }} />
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box> :
                <Grid container spacing={3}>
                    {filteredBursaries.map(bursary => (
                        <Grid item key={bursary.Id} xs={12} sm={6} md={4}>
                            <BursaryInfoCard 
                                bursary={bursary} onOpenEdit={handleOpenEditModal} onOpenRubric={handleOpenRubricModal}
                                onClone={handleOpenCloneModal} onArchive={handleOpenArchiveModal} onUnarchive={handleUnarchive}
                                onDelete={handleOpenDeleteModal} currentUserRole={user.role}
                            />
                        </Grid>
                    ))}
                     {filteredBursaries.length === 0 && (
                        <Grid item xs={12}><Typography sx={{textAlign: 'center', mt: 4, color: 'text.secondary'}}>No bursaries found.</Typography></Grid>
                    )}
                </Grid>
            }

            <Dialog open={isEditModalOpen} onClose={handleCloseModals} fullWidth maxWidth="md">
                <DialogTitle>{selectedBursary?.Id ? 'Edit Bursary' : 'Create New Bursary'}</DialogTitle>
                <DialogContent>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <TextField name="FundName" label="Bursary Name" value={selectedBursary?.FundName || ''} onChange={handleFormChange} fullWidth margin="dense" />
                            <TextField name="Sponsor" label="Sponsor" value={selectedBursary?.Sponsor || ''} onChange={handleFormChange} fullWidth margin="dense" />
                            
                            {user?.role === 'SuperUser' && (
                                <FormControl fullWidth margin="dense" required>
                                    <InputLabel>Assign to Tenant (Company)</InputLabel>
                                    <Select 
                                        name="CompanyId" 
                                        value={selectedBursary?.CompanyId || ''} 
                                        label="Assign to Tenant (Company)" 
                                        onChange={handleFormChange}
                                    >
                                        <MenuItem value="" disabled><em>Select a Tenant...</em></MenuItem>
                                        {companies.map(c => (
                                            <MenuItem key={c.Id} value={c.Id}>
                                                {c.CompanyName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControl fullWidth margin="dense">
                                <InputLabel>Fund Category</InputLabel>
                                <Select name="FundCategory" value={selectedBursary?.FundCategory || 'General'} label="Fund Category" onChange={handleFormChange}>
                                    {FUND_CATEGORIES.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                                </Select>
                            </FormControl>
                            
                            {/* 🚨 TENANT FEATURE TOGGLE: Hide Amount Input if requested */}
                            {tenantConfig.features.showFundAmount !== false && (
                                <TextField name="TotalAmount" label="Total Fund Amount" type="number" value={selectedBursary?.TotalAmount || ''} onChange={handleFormChange} fullWidth margin="dense" />
                            )}
                            
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField name="ApplicationStartDate" label="Start Date" type="date" value={selectedBursary?.ApplicationStartDate?.split('T')[0] || ''} onChange={handleFormChange} fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField name="ApplicationEndDate" label="End Date" type="date" value={selectedBursary?.ApplicationEndDate?.split('T')[0] || ''} onChange={handleFormChange} fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 2, mb: 6, bgcolor: 'background.paper' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Overview / Description</Typography>
                                <ReactQuill 
                                    theme="snow"
                                    modules={quillModules}
                                    value={selectedBursary?.Description || ''}
                                    onChange={(content) => setSelectedBursary(prev => ({ ...prev, Description: content }))}
                                    style={{ 
                                        height: '150px', 
                                        backgroundColor: 'transparent',
                                        color: 'inherit'
                                    }}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>Detailed Requirements</Typography>
                                
                                <TextField
                                    label="Eligibility Criteria List"
                                    multiline rows={6} fullWidth margin="dense"
                                    value={criteriaText} onChange={(e) => setCriteriaText(e.target.value)}
                                    placeholder="- Must be SA Citizen&#10;- Grade 12 Math > 60%&#10;- Combined income < R350k"
                                    helperText="Enter one requirement per line." sx={{ bgcolor: 'background.paper' }} 
                                />

                                <TextField
                                    label="Required Documents List"
                                    multiline rows={5} fullWidth margin="dense"
                                    value={documentsText} onChange={(e) => setDocumentsText(e.target.value)}
                                    placeholder="- Certified ID Copy&#10;- Matric Results&#10;- Proof of Income"
                                    helperText="Enter one document per line." sx={{ bgcolor: 'background.paper', mt: 2 }} 
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    
                    <FormControlLabel 
                        control={<Switch checked={selectedBursary?.IsActive || false} onChange={handleFormChange} name="IsActive" />} 
                        label="Bursary is Active (Visible to Applicants)" 
                        sx={{ mt: 2 }}
                    />

                </DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleSaveBursary} variant="contained">Save</Button></DialogActions>
            </Dialog>

            {selectedBursary && <RubricEditorModal open={isRubricModalOpen} onClose={handleCloseModals} fund={selectedBursary} token={token} />}

            <Dialog open={isCloneConfirmOpen} onClose={handleCloseModals}>
                <DialogTitle>Confirm Clone</DialogTitle>
                <DialogContent><DialogContentText>Clone <strong>"{actionBursary?.FundName}"</strong>?</DialogContentText></DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleConfirmClone} variant="contained">Clone</Button></DialogActions>
            </Dialog>

            <Dialog open={isArchiveConfirmOpen} onClose={handleCloseModals}>
                <DialogTitle>Confirm Archive</DialogTitle>
                <DialogContent><DialogContentText>Archive <strong>"{actionBursary?.FundName}"</strong>?</DialogContentText></DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleConfirmArchive} color="warning" variant="contained">Archive</Button></DialogActions>
            </Dialog>

            <Dialog open={isDeleteConfirmOpen} onClose={handleCloseModals}>
                <DialogTitle sx={{ color: 'error.main' }}>Permanently Delete?</DialogTitle>
                <DialogContent>
                    <DialogContentText>You are about to <strong>PERMANENTLY DELETE "{actionBursary?.FundName}"</strong>.</DialogContentText>
                    <Alert severity="error" sx={{ mt: 2 }}><strong>Warning:</strong> This will delete ALL associated applications, documents, payments, and history. This action cannot be undone.</Alert>
                </DialogContent>
                <DialogActions><Button onClick={handleCloseModals}>Cancel</Button><Button onClick={handleConfirmDelete} variant="contained" color="error" startIcon={<DeleteForeverIcon />}>Yes, Delete Forever</Button></DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}><Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
        </Container>
    );
};

export default BursaryManagementPage;